/**
 * Business rules API client.
 *
 * Requests are routed through the gateway (/api/v1/rules/*) which
 * proxies to the dynamic_rules_service.
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import type { BusinessRule } from '@/types';
import type { GeneratedRule, GenerateRuleResponse, RuleValidation } from '@/types/rule';
import axios from 'axios';

import type { BackendRuleResponse } from './ruleMapper';
import { extractValidation, mapRule, mapRules } from './ruleMapper';

const rulesApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

rulesApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

// ---------------------------------------------------------------------------
// Query params
// ---------------------------------------------------------------------------

export interface RuleListParams {
  department?: string;
  state?: string;
  entity_type?: string;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/** List business rules with optional server-side filters. */
export async function listRules(params?: RuleListParams): Promise<BusinessRule[]> {
  const { data } = await rulesApi.get<BackendRuleResponse[]>('/api/v1/rules', {
    params,
  });
  return mapRules(data);
}

/** Fetch a single rule by ID. */
export async function getRule(id: string): Promise<BusinessRule> {
  const { data } = await rulesApi.get<BackendRuleResponse>(`/api/v1/rules/${id}`);
  return mapRule(data);
}

// ---------------------------------------------------------------------------
// Rule generation (LLM) + creation
// ---------------------------------------------------------------------------

const queryRouterApi = axios.create({
  baseURL: config.QUERY_ROUTER_URL,
  timeout: 60_000, // LLM generation can take 10-30s
  headers: { 'Content-Type': 'application/json' },
});

queryRouterApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

/** Generate a structured rule from plain-English description via LLM. */
export async function generateRule(
  description: string,
  department?: string,
): Promise<GenerateRuleResponse> {
  const { data } = await queryRouterApi.post<GenerateRuleResponse>(
    '/rules/generate',
    { description, ...(department && { department }) },
  );
  return data;
}

/** Result of a rule create/update — the mapped rule plus optional validation metadata. */
export interface CreateRuleResult {
  rule: BusinessRule;
  validation: RuleValidation | null;
}

/** Create a new business rule in dynamic_rules_service. */
export async function createRule(rule: GeneratedRule): Promise<CreateRuleResult> {
  const { data } = await rulesApi.post<BackendRuleResponse>(
    '/api/v1/rules',
    rule,
  );
  return { rule: mapRule(data), validation: extractValidation(data) };
}

/** Update an existing business rule (creates SCD2 version on backend). */
export async function updateRule(
  id: string,
  data: Partial<GeneratedRule>,
): Promise<CreateRuleResult> {
  const { data: resp } = await rulesApi.put<BackendRuleResponse>(
    `/api/v1/rules/${id}`,
    data,
  );
  return { rule: mapRule(resp), validation: extractValidation(resp) };
}

/** Soft-delete a business rule (sets is_current=false, is_active=false). */
export async function deleteRule(id: string): Promise<void> {
  await rulesApi.delete(`/api/v1/rules/${id}`);
}
